/**
 * Domain Detection Utilities
 * Centralized domain detection logic for reliable routing
 */

export const CHAT_REALESTATE_DOMAINS = [
  'chat.realestate',
  'www.chat.realestate',
  'dev.chat.realestate',
  'kairos.realestate',
  'www.kairos.realestate'
];

export const AI_SEARCH_PORTAL_DOMAINS = [
  'aisearchportal.com',
  'www.aisearchportal.com',
  'dev.aisearchportal.com',
  'app.kairosiq.ai',
  'demo.kairosiq.ai',
  'dev.app.kairosiq.ai'
];

export const OFFERNOWAZ_DOMAINS = [
  'offernowaz.com',
  'www.offernowaz.com',
  'dev.offernowaz.com'
];

export const KAIROSIQ_DOMAINS = [
  'kairosiq.ai',
  'app.kairosiq.ai',
  'demo.kairosiq.ai',
  'dev.kairosiq.ai',
  'dev.app.kairosiq.ai',
  'agentkairos.com',
  'app.agentkairos.com',
  'dev.agentkairos.com'
];

/**
 * Get the current domain without protocol or path
 */
export const getCurrentDomain = (): string => {
  if (typeof window === 'undefined') return '';
  return window.location.hostname.toLowerCase();
};

/**
 * Check if current domain is chat.realestate or variants
 */
export const isChatRealEstateDomain = (): boolean => {
  const domain = getCurrentDomain();
  
  // Check direct match
  if (CHAT_REALESTATE_DOMAINS.includes(domain)) {
    console.log('✅ Direct chat.realestate domain match:', domain);
    return true;
  }
  
  // Check for local development with override
  if (domain === 'localhost' || domain === '127.0.0.1') {
    // Check for test parameter
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('portal') === 'chat') {
      console.log('✅ Local development with portal=chat parameter');
      return true;
    }
    
    // Check for specific routes that should use chat.realestate
    const chatRoutes = ['/deal-report/', '/cma-report/', '/property/', '/homes-for-sale/'];
    const currentPath = window.location.pathname;
    if (chatRoutes.some(route => currentPath.startsWith(route))) {
      console.log('✅ Local development with chat.realestate route:', currentPath);
      return true;
    }
  }
  
  // Check for /etc/hosts override (separate from localhost check)
  if (domain === 'local.chat.realestate') {
    console.log('✅ Local development with /etc/hosts override');
    return true;
  }
  
  console.log('❌ Not a chat.realestate domain:', domain);
  return false;
};

/**
 * Check if current domain is AISearchPortal
 */
export const isAISearchPortalDomain = (): boolean => {
  const domain = getCurrentDomain();
  
  if (AI_SEARCH_PORTAL_DOMAINS.includes(domain)) {
    console.log('✅ AISearchPortal domain detected:', domain);
    return true;
  }
  
  // Development override
  if ((domain === 'localhost' || domain === '127.0.0.1') && window.location.pathname.startsWith('/portal/')) {
    console.log('✅ Local development with /portal/ path');
    return true;
  }
  
  return false;
};

/**
 * Check if current domain is OfferNowAZ
 */
export const isOfferNowAZDomain = (): boolean => {
  const domain = getCurrentDomain();
  
  if (OFFERNOWAZ_DOMAINS.includes(domain)) {
    console.log('✅ OfferNowAZ domain detected:', domain);
    return true;
  }
  
  // Development override
  if ((domain === 'localhost' || domain === '127.0.0.1') && window.location.pathname.startsWith('/offernowaz/')) {
    console.log('✅ Local development with /offernowaz/ path');
    return true;
  }
  
  return false;
};

/**
 * Check if current domain is a white-label domain
 */
export const isWhiteLabelDomain = (): boolean => {
  const domain = getCurrentDomain();
  
  // Skip known domains
  const knownDomains = [
    ...KAIROSIQ_DOMAINS,
    ...CHAT_REALESTATE_DOMAINS,
    ...AI_SEARCH_PORTAL_DOMAINS,
    ...OFFERNOWAZ_DOMAINS,
    'localhost',
    '127.0.0.1'
  ];
  
  if (knownDomains.includes(domain)) {
    return false;
  }
  
  // All other domains are potential white-label domains
  console.log('✅ White-label domain detected:', domain);
  return true;
};

/**
 * Get the portal type based on domain
 */
export type PortalType = 'kairosiq' | 'chat-realestate' | 'ai-search-portal' | 'offernowaz' | 'white-label';

export const getPortalType = (): PortalType => {
  if (isChatRealEstateDomain()) return 'chat-realestate';
  if (isAISearchPortalDomain()) return 'ai-search-portal';
  if (isOfferNowAZDomain()) return 'offernowaz';
  if (isWhiteLabelDomain()) return 'white-label';
  return 'kairosiq';
};

/**
 * Log domain detection info for debugging
 */
export const logDomainInfo = (): void => {
  console.log('🌐 Domain Detection Info:', {
    hostname: getCurrentDomain(),
    pathname: window.location.pathname,
    search: window.location.search,
    portalType: getPortalType(),
    isChatRealEstate: isChatRealEstateDomain(),
    isAISearchPortal: isAISearchPortalDomain(),
    isOfferNowAZ: isOfferNowAZDomain(),
    isWhiteLabel: isWhiteLabelDomain()
  });
};